import { useEffect, useState } from "react";
import AdminLayout from "../../layout/AdminLayoutSewa";
import { adminDashboardSummary } from "../../api";

const numericValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value) =>
  numericValue(value).toLocaleString("en-MY", {
    maximumFractionDigits: 0,
  });

const formatCurrency = (value) =>
  `RM ${numericValue(value).toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const StatCard = ({ title, value, color = "blue" }) => {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
    red: "bg-red-50 text-red-700 border-red-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  };

  return (
    <div className={`p-5 rounded-xl border ${colorMap[color] || colorMap.blue}`}>
      <div className="text-xs font-medium text-gray-500 uppercase">{title}</div>
      <div className="text-3xl font-bold mt-2">{formatNumber(value)}</div>
    </div>
  );
};

const MoneyCard = ({ title, value, color = "blue" }) => {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className={`p-5 rounded-xl border ${colorMap[color] || colorMap.blue}`}>
      <div className="text-xs font-medium text-gray-500 uppercase">{title}</div>
      <div className="text-3xl font-bold mt-2">{formatCurrency(value)}</div>
    </div>
  );
};

const InfoBox = ({ title, rows }) => {
  return (
    <div className="bg-white rounded-xl border p-5">
      <div className="text-lg font-semibold text-gray-800 mb-4">{title}</div>
      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between items-center border-b pb-2 last:border-b-0">
            <span className="text-sm text-gray-600">{row.label}</span>
            <span className="font-semibold text-gray-800">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Landing = () => {
  const [data, setData] = useState({
    total_agents: 0,
    total_assigned_units: 0,
    total_bills: 0,
    paid_bills: 0,
    unpaid_bills: 0,
    overdue_bills: 0,
    total_bill_amount: 0,
    total_paid_amount: 0,
    total_overdue_amount: 0,
  });
  const [loading, setLoading] = useState(true);

  const handleData = async () => {
    try {
      setLoading(true);
      const res = await adminDashboardSummary();
      setData(res || {});
    } catch (error) {
      console.log("dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleData();
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Dashboard Admin Sewa</h1>
            <p className="text-sm text-gray-500 mt-1">
              Ringkasan ejen, tugasan unit, bayaran dan tunggakan.
            </p>
          </div>

          <button
            onClick={handleData}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <StatCard title="Jumlah Ejen" value={data.total_agents} color="blue" />
          <StatCard title="Unit Ditugaskan" value={data.total_assigned_units} color="purple" />
          <StatCard title="Jumlah Bil" value={data.total_bills} color="indigo" />
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <StatCard title="Bil Paid" value={data.paid_bills} color="green" />
          <StatCard title="Bil Unpaid" value={data.unpaid_bills} color="yellow" />
          <StatCard title="Bil Overdue" value={data.overdue_bills} color="red" />
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <MoneyCard title="Jumlah Nilai Bil" value={data.total_bill_amount} color="blue" />
          <MoneyCard title="Jumlah Kutipan" value={data.total_paid_amount} color="green" />
          <MoneyCard title="Jumlah Tunggakan" value={data.total_overdue_amount} color="red" />
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <InfoBox
            title="Ringkasan Operasi"
            rows={[
              { label: "Jumlah Ejen Aktif", value: formatNumber(data.total_agents) },
              { label: "Jumlah Unit Ditugaskan", value: formatNumber(data.total_assigned_units) },
              { label: "Jumlah Rekod Bil", value: formatNumber(data.total_bills) },
              { label: "Jumlah Bil Overdue", value: formatNumber(data.overdue_bills) },
            ]}
          />

          <InfoBox
            title="Ringkasan Kewangan"
            rows={[
              { label: "Jumlah Nilai Bil", value: formatCurrency(data.total_bill_amount) },
              { label: "Jumlah Kutipan", value: formatCurrency(data.total_paid_amount) },
              { label: "Jumlah Tunggakan", value: formatCurrency(data.total_overdue_amount) },
              {
                label: "Paid / Unpaid",
                value: `${formatNumber(data.paid_bills)} / ${formatNumber(data.unpaid_bills)}`,
              },
            ]}
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default Landing;
